import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ArrowRight, FileText, Loader, Upload } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  analyzeMeeting,
  extractParticipants,
} from '../../services/meetingAnalysis';
import { processFile } from '../../utils/file-utils';
import ParticipantSelectionModal from './ParticipantSelectionModal';

interface SampleMeeting {
  title: string;
  description: string;
  url: string;
}

export default function MeetingCallAnalyzer() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileProcessingError, setFileProcessingError] = useState<string | null>(
    null,
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [transcript, setTranscript] = useState<string>('');
  const [participants, setParticipants] = useState<
    { id: string; name: string }[]
  >([]);

  // Example meeting scenarios
  const sampleMeetings: SampleMeeting[] = [
    {
      title: 'Handling difficult team member',
      description: 'Delivering feedback to a defensive team member.',
      url: '/meetingcall/results/67d32474539722f75bc66027',
    },
    {
      title: 'Aligning with your boss',
      description: 'Managing expectations in a 1:1 with your boss.',
      url: '/meetingcall/results/67d3253f539722f75bc66035',
    },
    {
      title: 'Managing underperformer',
      description:
        'Discussing performance issues and setting improvement expectations.',
      url: '/meetingcall/results/67d325a2539722f75bc66041',
    },
  ];

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        await handleFileProcess(selectedFile);
      }
    },
    [],
  );

  const handleFileProcess = async (file: File) => {
    setIsProcessing(true);
    setFileProcessingError(null);

    try {
      // Display info about the type of file being processed
      const fileType = file.name.toLowerCase().endsWith('.docx')
        ? 'Word document'
        : file.name.toLowerCase().endsWith('.doc')
        ? 'Word document'
        : 'text file';

      toast.info(`Processing ${fileType}...`);

      // Read the file content
      const transcriptText = await processFile(file);

      if (!transcriptText || transcriptText.trim() === '') {
        throw new Error(
          'The file appears to be empty or could not be properly read.',
        );
      }

      setTranscript(transcriptText);

      // Extract participants
      toast.info('Identifying meeting participants...');
      const extractedParticipants = await extractParticipants(transcriptText);

      if (extractedParticipants.length === 0) {
        throw new Error(
          'No meeting participants could be identified in the transcript.',
        );
      }

      setParticipants(extractedParticipants);
      toast.success(
        `Successfully processed file with ${extractedParticipants.length} participants detected`,
      );
    } catch (error) {
      console.error('Error processing file:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to process file. Please try a different file.';

      setFileProcessingError(errorMessage);
      toast.error(errorMessage);
      setTranscript('');
      setParticipants([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];

      // Check if the file is a valid file type
      if (!droppedFile.name.toLowerCase().match(/\.(txt|doc|docx)$/)) {
        toast.error('Please upload a .txt, .doc, or .docx file');
        return;
      }

      setFile(droppedFile);
      await handleFileProcess(droppedFile);
    }
  }, []);

  const handleBrowseClick = useCallback(() => {
    document.getElementById('file-upload')?.click();
  }, []);

  const performAnalysis = useCallback(
    async (selectedParticipant: string, participants: string[]) => {
      setIsAnalyzing(true);
      try {
        const { analysisId } = await analyzeMeeting(
          transcript,
          selectedParticipant,
          participants,
        );
        // Now we're not storing in local state, the API handles DB storage
        return analysisId;
      } catch (error) {
        console.error('Error analyzing meeting:', error);
        toast.error('Failed to analyze meeting. Please try again.');
        throw error;
      } finally {
        setIsAnalyzing(false);
      }
    },
    [transcript],
  );

  const handleAnalysisComplete = useCallback(
    async (participantId: string) => {
      const selectedParticipant = participants.find(
        (p) => p.id === participantId,
      );
      if (!selectedParticipant) return;

      try {
        // Perform the analysis - this now saves to the database via API
        const analysisId = await performAnalysis(
          selectedParticipant.name,
          participants.map((p) => p.name),
        );

        // Hide the modal
        setShowParticipantModal(false);

        // Navigate to results page with the database ID
        navigate(`/meetingcall/results/${analysisId}`);
      } catch (error) {
        console.error('Analysis failed:', error);
        toast.error('Analysis failed. Please try again.');
      }
    },
    [navigate, participants, performAnalysis],
  );

  const handleGetInsights = useCallback(() => {
    if (file && participants.length > 0) {
      // Show the selection modal
      setShowParticipantModal(true);
    } else if (file && participants.length === 0) {
      toast.error(
        'No participants found in the transcript. Please check the file format or try a different file.',
      );
    }
  }, [file, participants]);

  const resetFileUpload = useCallback(() => {
    setFile(null);
    setTranscript('');
    setParticipants([]);
    setFileProcessingError(null);
  }, []);

  return (
    <div className="min-h-screen bg-[#F6F8F8] p-6 sm:p-10">
      {/* Hupo Logo */}
      <div className="mb-12">
        <img
          src="https://coach.hupo.co/logos/trademarked/orange/logo.svg"
          alt="Hupo AI"
          className="h-8"
        />
      </div>

      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Upload a 1:1 meeting transcript & get instant insights
        </h1>
        <p className="text-gray-600 text-sm mb-8">
          Uncover hidden patterns in your meetings—gain insights to lead,
          communicate, and influence more effectively.
        </p>

        {/* File Upload Area */}
        <div
          className={cn(
            'border-2 bg-white border-dashed rounded-xl p-12 flex flex-col items-center justify-center mb-6 transition-colors cursor-pointer hover:border-primary hover:bg-primary/5',
            isDragging ? 'border-primary bg-primary/5' : 'border-gray-200',
            fileProcessingError ? 'border-red-300' : '',
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-upload"
            className="hidden"
            accept=".txt,.doc,.docx"
            onChange={handleFileChange}
          />

          {!file ? (
            <>
              <div className="mb-4">
                <Upload size={24} className="text-gray-400" />
              </div>

              <div className="text-center">
                <p className="text-gray-700 mb-1">
                  <b>Drop your meeting transcript here</b> or
                  <button
                    className="text-blue-500 font-medium ml-1"
                    onClick={handleBrowseClick}
                  >
                    Browse
                  </button>
                </p>
                <p className="text-gray-500 text-sm">
                  Upload .txt, .doc, or .docx files
                </p>
              </div>
            </>
          ) : isProcessing ? (
            <div className="flex flex-col items-center">
              <Loader size={24} className="animate-spin mb-2 text-primary" />
              <p className="text-center text-gray-800">
                Processing transcript...
              </p>
            </div>
          ) : fileProcessingError ? (
            <div className="flex flex-col items-center">
              <div className="text-red-500 mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <p className="text-center text-red-600 font-medium mb-2">
                {fileProcessingError}
              </p>
              <button
                onClick={resetFileUpload}
                className="mt-2 text-blue-500 text-sm hover:text-blue-700"
              >
                Try another file
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <FileText size={24} className="mb-2 text-primary" />
              <p className="text-center text-gray-800 font-medium">
                {file.name}
              </p>
              <p className="text-center text-gray-500 text-sm mt-1">
                {participants.length} participants detected
              </p>
              <button
                onClick={resetFileUpload}
                className="mt-2 text-red-500 text-sm hover:text-red-700"
              >
                Remove
              </button>
            </div>
          )}
        </div>

        {/* Get Insights Button */}
        <div className="flex justify-center mb-12">
          <Button
            onClick={handleGetInsights}
            disabled={
              !file ||
              isProcessing ||
              isAnalyzing ||
              participants.length === 0 ||
              !!fileProcessingError
            }
            className={cn(
              'px-12 py-3 rounded-full transition-colors font-medium',
              file &&
                !isProcessing &&
                !isAnalyzing &&
                participants.length > 0 &&
                !fileProcessingError
                ? 'bg-primary hover:bg-primary/90 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800',
            )}
          >
            {isAnalyzing
              ? 'Analyzing...'
              : isProcessing
              ? 'Processing...'
              : participants.length === 0 &&
                file &&
                !isProcessing &&
                !fileProcessingError
              ? 'No participants found'
              : 'Get insights'}
          </Button>
        </div>

        {/* Example Meetings */}
        <div className="mt-12">
          <h2 className="text-gray-700 text-sm  mb-4">
            Don't have a transcript handy? Check out these ready meeting
            examples
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sampleMeetings.map((example, index) => (
              <div
                key={index}
                className="border bg-white border-gray-200 rounded-xl p-4 hover:shadow-md transition-all duration-200 hover:-translate-y-1"
              >
                <h3 className="font-semibold mb-2">{example.title}</h3>
                <p className="text-gray-600 text-sm mb-4">
                  {example.description}
                </p>
                <button
                  onClick={() => navigate(example.url)}
                  className="text-primary flex items-center text-sm  hover:underline"
                >
                  View insight <ArrowRight size={16} className="ml-1" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Participant Selection Modal */}
      <ParticipantSelectionModal
        isOpen={showParticipantModal}
        participants={participants}
        onClose={() => setShowParticipantModal(false)}
        onSelect={handleAnalysisComplete}
        onAnalyze={performAnalysis}
      />
    </div>
  );
}
